import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useUserContext } from "../Context/UserContext";

/** Unsent discussion text stays in this browser tab, separately for each participant. */
export function useMessageDraft(id: string | undefined, data: any) {
  const { user } = useUserContext();
  const actor = user?.id || data?.viewerId;
  const key =
    id && actor && data?.review?.id === id
      ? `reviewed.message-draft.${actor}.${id}`
      : null;
  const [body, setBody] = useState("");
  const [saved, setSaved] = useState(true);
  useLayoutEffect(() => {
    setBody("");
    setSaved(true);
    if (!key) return;
    try {
      const entry = JSON.parse(sessionStorage.getItem(key) || "null");
      if (typeof entry?.body === "string") setBody(entry.body);
      else sessionStorage.removeItem(key);
    } catch {
      setSaved(false);
    }
  }, [key]);
  const change = useCallback(
    (value: string) => {
      setBody(value);
      try {
        if (!key) throw new Error("Draft unavailable");
        if (value)
          sessionStorage.setItem(
            key,
            JSON.stringify({ body: value, at: Date.now() })
          );
        else sessionStorage.removeItem(key);
        setSaved(true);
      } catch {
        setSaved(false);
      }
    },
    [key]
  );
  const clear = useCallback(() => change(""), [change]);
  useEffect(() => {
    if (!body || saved) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    // Also guard in-app links when browser recovery storage is unavailable.
    const guard = (event: MouseEvent) => {
      if (
        !(event.target instanceof Element) ||
        !event.target.closest("a[href]")
      )
        return;
      if (
        !window.confirm(
          "Your message draft could not be saved. Leave and discard it?"
        )
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", warn);
    document.addEventListener("click", guard, true);
    return () => {
      window.removeEventListener("beforeunload", warn);
      document.removeEventListener("click", guard, true);
    };
  }, [body, saved]);
  return { body, change, clear, saved };
}
